
import { useStore } from "vuex";
import { computed, ComputedRef, onBeforeMount, Ref, ref } from "vue";
import { Interest, User } from "../../../domain/entities/entities";
import { useRouter } from "vue-router";
import {
  dispatchErrorBox,
  isCirculoTemplate,
  maskCEP,
  maskCPF
} from "../components/mixins";
import { wrapLoading } from "../utils/wrap_loading";
import GrayInput from "../components/application/forms/GrayInput.vue";
import { dispatchError } from "../utils/error_handling";
import http from "../../repository/http";
import { States } from "../../../domain/entities/states";
import CropperImageInput from "../components/application/CropperImageInput.vue";
import MaskInput from "../components/application/inputs/MaskInput.vue";
import CardSelect from "../components/application/buttons/CardSelect.vue";
import NumberInput from "../components/application/inputs/NumberInput.vue";
import PersonPhoto from "../components/application/data-table/PersonPhoto.vue";

export default {
  methods: { isCirculoTemplate },
  components: {
    PersonPhoto,
    NumberInput,
    CardSelect,
    MaskInput,
    CropperImageInput,
    GrayInput
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const interests: Ref<Interest[]> = ref([]);
    const user: Ref<User> = ref(new User());
    const states: Ref<States[]> = ref([]);
    const isInputImageOpened: ComputedRef<boolean> = computed(
      () => store.getters["utils_module/isVisible"]
    );

    const userEmail: Ref<string> = ref("");
    const userDocument: Ref<string> = ref("");
    onBeforeMount(async () => {
      try {
        await wrapLoading(async () => {
          user.value = await loadUser();
          userEmail.value = user.value.email;
          userDocument.value = user.value.document;
          user.value.interests = await listUserInterest();
          if (user.value.document) {
            user.value.document = maskCPF(user.value.document);
          }

          if (user.value.address.zipCode) {
            user.value.address.zipCode = maskCEP(user.value.address.zipCode);
          }
          interests.value = await loadInterests();
          states.value = await loadStates();
          removeInterestEquals();
        });
      } catch (e) {
        console.error(e);
        await dispatchError(e);
      }
    });

    /** Loads the user information such as usage statistics and courses completion/progress. */
    const loadUser = async (): Promise<User> => {
      await store.dispatch("userProfile_module/loadUser");
      return store.getters["userProfile_module/user"];
    };

    const loadInterests = async (): Promise<Interest[]> => {
      await store.dispatch("interest_module/loadInterests");
      return store.getters["interest_module/interests"];
    };

    const loadStates = async (): Promise<States[]> => {
      const response = await http.get("api/decola/states");
      return response.data.states;
    };

    /** Updates user personal data with the information provided through the form. */
    async function saveChanges(): Promise<void> {
      try {
        await wrapLoading(async () => {
          if (user.value.document) {
            user.value.document = removeMask(user.value.document);
          }
          if (user.value.address.zipCode) {
            user.value.address.zipCode = removeMask(user.value.address.zipCode);
          }
          await store.dispatch("userProfile_module/editUser", user.value);

          await store.dispatch(
            "userProfile_module/editUserInterests",
            user.value.interests.map((interest: Interest) => interest.id)
          );

          await router.push({
            name: "profile",
            params: { option: "statistics" }
          });
        });
      } catch (e) {
        console.error(e);
        await dispatchError(e);
      }
    }

    async function discardChanges(): Promise<void> {
      await router.push({ name: "profile", params: { option: "statistics" } });
    }

    async function listUserInterest(): Promise<Interest[]> {
      await store.dispatch("interest_module/listUserInterest");
      return store.getters["interest_module/interests"];
    }

    async function requestCepAPI(cep: string) {
      if (cep.length < 9) {
        user.value.address.zipCode = cep;
        return;
      }
      try {
        const response = await http.get(`/api/decola/cep/${cep}`);
        user.value.address.federalUnit = response.data.federalUnit;
        user.value.address.city = response.data.city;
        user.value.address.district = response.data.district;
        user.value.address.street = response.data.street;
        user.value.address.stateID = response.data.stateID;
        const addressNumberInput = document.getElementById(
          "addressNumber"
        ) as HTMLInputElement;
        addressNumberInput.focus();
      } catch (e) {
        await dispatchErrorBox(e?.response?.data?.message || e?.message);
        user.value.address.zipCode = "";
      }
    }

    function addEmptyValue(input: HTMLInputElement) {
      user.value.address.zipCode = input.value;
    }

    const removeMask = (value: string): string => {
      return value.replace(/\D+/g, "");
    };

    const addInterest = (interest: Interest, index: number): void => {
      if (user.value.interests?.length < 5) {
        user.value.interests.push(interest);
        interests.value.splice(index, 1);
      }
    };
    const removeInterest = (interest: Interest, index: number): void => {
      interests.value.push(interest);
      user.value.interests.splice(index, 1);
    };
    const removeInterestEquals = (): void => {
      user.value.interests.forEach((userInterest: Interest) => {
        interests.value.forEach((interest: Interest, index: number) => {
          if (interest.id == userInterest.id) {
            interests.value.splice(index, 1);
          }
        });
      });
    };

    /** Loads a fallback image when an image rendering error occurs. */
    const loadFallbackImage = (event: Event) => {
      const img = event.target as HTMLImageElement;
      img.src = require("../assets/static/icons/onerror-user.png");
    };

    const openInputImage = async (): Promise<void> => {
      await store.dispatch("utils_module/setVisibility", true);
    };

    const getCroppedImageUrl = (url: string) => {
      user.value.image = url;
    };

    return {
      user,
      interests,
      states,
      isInputImageOpened,
      userDocument,
      userEmail,
      saveChanges,
      discardChanges,
      requestCepAPI,
      addEmptyValue,
      addInterest,
      removeInterest,
      loadFallbackImage,
      openInputImage,
      getCroppedImageUrl
    };
  }
};
